import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, TrendingUp } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a marketing consultant invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A marketing consultant invoice should include your name or agency name, the client name and campaign or project name, a unique invoice number and date, payment due date, a breakdown of services (strategy, content, ads management, reporting), your rate (hourly, daily, or monthly retainer), any media spend or third-party costs passed through, applicable tax, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do marketing consultants typically structure their fees?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Marketing consultants commonly use three structures: monthly retainers for ongoing strategy and execution work; project fees for defined campaigns or audits; and day rates for strategy workshops and consulting days. Many combine a base retainer with performance-based bonuses. Whichever structure you use, itemise your invoice clearly so the client can see exactly what each fee covers.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should media spend be included on a marketing invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It depends on your arrangement. If you manage ad spend on behalf of the client from your own account, invoice the spend as a pass-through cost with receipts. If the client pays ad platforms directly, do not include it on your invoice. Never mix your consulting fee with client media spend without clear labelling — it creates confusion and audit issues.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do marketing consultants need to charge VAT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Only if your annual turnover exceeds the threshold in your country. In the UK, VAT at 20% applies once turnover exceeds £90,000. In India, marketing and advertising services attract 18% GST above ₹20 lakh turnover. In Australia, 10% GST applies above AUD $75,000. In the US, marketing consulting services are generally not subject to sales tax, though some states tax digital advertising services.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this marketing consultant invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free marketing consultant invoice template. Fill in your project or retainer details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function MarketingConsultantInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Marketing Consultant Invoice Template: Free PDF | Invilic"
        description="Free marketing consultant invoice template. Bill for strategy, retainers, campaigns, and ad management. Download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/marketing-consultant-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-700 dark:text-fuchsia-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Marketing Consultant Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for marketing consultants and digital marketing freelancers. Bill for strategy, campaigns, and retainers. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-fuchsia-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Marketing Professional
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Digital marketing consultants', desc: 'Bill for SEO, PPC, email marketing, and growth strategy by the month or project.' },
              { title: 'Social media managers', desc: 'Invoice for content creation, scheduling, community management, and paid social campaigns.' },
              { title: 'Brand strategists', desc: 'Bill for brand audits, positioning workshops, messaging frameworks, and go-to-market plans.' },
              { title: 'Performance marketers', desc: 'Issue invoices for paid ads management, funnel optimisation, and conversion rate consulting.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-fuchsia-100 dark:bg-fuchsia-900/40 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Marketing Consultant Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name / agency name and logo',
              'Client name and campaign name',
              'Invoice number and date',
              'Payment due date',
              'Services rendered per line item',
              'Monthly retainer or project fee',
              'Media spend pass-through (if applicable)',
              'Strategy and consulting hours',
              'Subtotal, tax (VAT/GST) and discount',
              'Total amount due',
              'Bank details or payment link',
              'Notes and reporting period',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-fuchsia-100 dark:border-fuchsia-800/30 bg-fuchsia-50 dark:bg-fuchsia-900/10">
                <CheckCircle className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Marketing Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your services', desc: 'Enter your retainer or project details, services, and any media costs.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-fuchsia-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Marketing Consultant Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Marketing Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-fuchsia-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Marketing Consultant Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
